import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type { Asset, Entry } from 'contentful';

/**
 * Returns the contentful data for HSA/FSA.
 */
export const useFlexContent = (content: FlexContent | undefined) => {
  const { formatMessage } = useIntl();

  return useMemo(() => {
    const title = content?.title ?? formatMessage(fallbacks.title);
    const eligibilityCta = content?.eligibilityCta ?? formatMessage(fallbacks.eligibilityCta);
    const disclaimerLabel = content?.disclaimerLabel ?? formatMessage(fallbacks.disclaimerLabel);
    const disclaimerDescription =
      content?.disclaimerDescription ?? formatMessage(fallbacks.disclaimerDescription);
    const heroImageXsUrl = content?.heroImageXs?.fields?.file?.url;
    const heroImageXsA11y = content?.heroImageXs?.fields?.description;
    const heroImageSmUrl = content?.heroImageSm?.fields?.file?.url;
    const heroImageSmA11y = content?.heroImageSm?.fields?.description;
    const guideTitle = content?.guideTitle ?? formatMessage(fallbacks.guideTitle);
    const guideEntries = mapGuideEntries(content?.guideEntries);
    const banner = content?.banner ?? formatMessage(fallbacks.banner);
    const coverageHint = content?.coverageHint ?? formatMessage(fallbacks.coverageHint);
    const coverageTitle = content?.coverageTitle ?? formatMessage(fallbacks.coverageTitle);
    const coverageDescription =
      content?.coverageDescription ?? formatMessage(fallbacks.coverageDescription);
    const coverageImageUrl = content?.coverageImage?.fields?.file?.url;
    const coverageImageA11y = content?.coverageImage?.fields?.description;
    const faqTitle = content?.faqTitle ?? formatMessage(fallbacks.faqTitle);
    const faqDescription = content?.faqDescription ?? formatMessage(fallbacks.faqDescription);
    const mappedFaqEntries = mapFaqEntries(content?.faqEntries);
    const faqEntries =
      mappedFaqEntries.length > 0 ? mappedFaqEntries : getFallbackFaqEntries(formatMessage);

    return {
      title,
      eligibilityCta,
      disclaimerLabel,
      disclaimerDescription,
      heroImageXsUrl,
      heroImageXsA11y,
      heroImageSmUrl,
      heroImageSmA11y,
      guideTitle,
      guideEntries,
      banner,
      coverageHint,
      coverageTitle,
      coverageDescription,
      coverageImageUrl,
      coverageImageA11y,
      faqTitle,
      faqDescription,
      faqEntries,
    };
  }, [content, formatMessage]);
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function mapGuideEntries(entries: FlexContent['guideEntries']) {
  return (
    entries?.flatMap((entry) => {
      const title = entry?.fields?.title;
      const description = entry?.fields?.description;
      const imageUrl = entry?.fields?.image?.fields?.file?.url;
      const imageA11y = entry?.fields?.image?.fields?.description || title;

      if (!title || !description || !imageUrl || !imageA11y) return [];

      return [{ id: entry.sys.id, title, description, imageUrl, imageA11y }];
    }) ?? []
  );
}

function mapFaqEntries(entries: FlexContent['faqEntries']) {
  return (
    entries?.flatMap((entry) => {
      const title = entry?.fields?.title;
      const description = entry?.fields?.description;

      if (!title || !description) return [];

      return [{ id: entry.sys.id, title, description }];
    }) ?? []
  );
}

function getFallbackFaqEntries(formatMessage: ReturnType<typeof useIntl>['formatMessage']) {
  return [
    {
      id: 'relationship',
      title: formatMessage(fallbacks.faqRelationshipTitle),
      description: formatMessage(fallbacks.faqRelationshipDescription),
    },
    {
      id: 'payment',
      title: formatMessage(fallbacks.faqPaymentTitle),
      description: formatMessage(fallbacks.faqPaymentDescription),
    },
    {
      id: 'availability',
      title: formatMessage(fallbacks.faqAvailabilityTitle),
      description: formatMessage(fallbacks.faqAvailabilityDescription),
    },
    {
      id: 'declined',
      title: formatMessage(fallbacks.faqDeclinedTitle),
      description: formatMessage(fallbacks.faqDeclinedDescription),
    },
    {
      id: 'splitting',
      title: formatMessage(fallbacks.faqSplittingTitle),
      description: formatMessage(fallbacks.faqSplittingDescription),
    },
  ];
}

// ─── Fallbacks ───────────────────────────────────────────────────────────────

const fallbacks = defineMessages({
  title: {
    defaultMessage: 'Your health account just unlocked the menu.',
    description: 'HSA/FSA | Hero | Title',
  },
  eligibilityCta: {
    defaultMessage: 'Check your eligibility',
    description: 'HSA/FSA | Hero | Eligibility CTA',
  },
  disclaimerLabel: {
    defaultMessage: 'HSA & FSA APPROVED PARTNERSHIP',
    description: 'HSA/FSA | Disclaimer | Label',
  },
  disclaimerDescription: {
    defaultMessage:
      'Sweetgreen has partnered with Flex to make checking out with your pre-tax dollars completely seamless. Fuel your week with fresh, nutrient-dense ingredients funded directly by your health savings.',
    description: 'HSA/FSA | Disclaimer | Description',
  },
  guideTitle: {
    defaultMessage: 'How to Maximize Your Pre-Tax Dollars',
    description: 'HSA/FSA | Guide | Title',
  },
  banner: {
    defaultMessage:
      'Because real, whole foods belong in your daily health budget. No loopholes, no endless receipt-hunting. Just clean fuel.',
    description: 'HSA/FSA | Banner',
  },
  coverageHint: {
    defaultMessage: "WHAT'S COVERED?",
    description: 'HSA/FSA | Coverage | Hint',
  },
  coverageTitle: {
    defaultMessage: 'Clinically driven meals',
    description: 'HSA/FSA | Coverage | Title',
  },
  coverageDescription: {
    defaultMessage:
      'Sweetgreen has partnered with Flex to make checking out with your pre-tax dollars completely seamless. Fuel your week with fresh, nutrient-dense ingredients funded directly by your health savings.',
    description: 'HSA/FSA | Coverage | Description',
  },
  faqTitle: {
    defaultMessage: 'Frequently asked questions',
    description: 'HSA/FSA | FAQ | Title',
  },
  faqDescription: {
    defaultMessage:
      'Everything you need to know about ordering and using your HSA/FSA card with Flex.',
    description: 'HSA/FSA | FAQ | Description',
  },
  faqRelationshipTitle: {
    defaultMessage: 'What is Flex and what is their relationship with Sweetgreen?',
    description: 'HSA/FSA | FAQ | Flex relationship | Title',
  },
  faqRelationshipDescription: {
    defaultMessage:
      'Flex is a partner that helps you manage your HSA/FSA benefits and pay for eligible items with your card. They work with Sweetgreen to ensure a seamless checkout experience for qualified purchases.',
    description: 'HSA/FSA | FAQ | Flex relationship | Description',
  },
  faqPaymentTitle: {
    defaultMessage: 'How do I pay with my HSA or FSA card?',
    description: 'HSA/FSA | FAQ | Payment | Title',
  },
  faqPaymentDescription: {
    defaultMessage:
      'Simply select your HSA/FSA card as the payment method at checkout. If your card is linked to your account, you can also use it for eligible purchases automatically.',
    description: 'HSA/FSA | FAQ | Payment | Description',
  },
  faqAvailabilityTitle: {
    defaultMessage: "What if I don't have my HSA/FSA card available?",
    description: 'HSA/FSA | FAQ | Card availability | Title',
  },
  faqAvailabilityDescription: {
    defaultMessage:
      "If you don't have your physical card, you can still use your digital card or account details to complete the purchase. You can also contact your provider for assistance.",
    description: 'HSA/FSA | FAQ | Card availability | Description',
  },
  faqDeclinedTitle: {
    defaultMessage: 'Why is my HSA/FSA card being declined?',
    description: 'HSA/FSA | FAQ | Declined card | Title',
  },
  faqDeclinedDescription: {
    defaultMessage:
      'Your card may be declined if the item is not eligible, your balance is insufficient, or the card has expired. Check your account status or contact your provider for more details.',
    description: 'HSA/FSA | FAQ | Declined card | Description',
  },
  faqSplittingTitle: {
    defaultMessage: 'I would like to use multiple HSA/FSA cards to pay for an item. Can I do that?',
    description: 'HSA/FSA | FAQ | Split payment | Title',
  },
  faqSplittingDescription: {
    defaultMessage:
      'Yes, you can split payments between multiple eligible cards. Our system will automatically apply the balance from each card to the total purchase amount.',
    description: 'HSA/FSA | FAQ | Split payment | Description',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type GuideEntry = Entry<{
  contentfulEntryTitle?: string;
  title?: string;
  description?: string;
  image?: Asset;
}>;

type FaqEntry = Entry<{
  contentfulEntryTitle?: string;
  title?: string;
  description?: string;
}>;

export type FlexContent = {
  title?: string;
  eligibilityCta?: string;
  disclaimerLabel?: string;
  disclaimerDescription?: string;
  heroImageXs?: Asset;
  heroImageSm?: Asset;
  guideTitle?: string;
  guideEntries?: GuideEntry[];
  banner?: string;
  coverageHint?: string;
  coverageTitle?: string;
  coverageDescription?: string;
  coverageImage?: Asset;
  faqTitle?: string;
  faqDescription?: string;
  faqEntries?: FaqEntry[];
};
